import './FilterForm.css';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import darkCalenderIcon from '../../assets/icon/darkCalenderIcon.svg';
import { CloudProviderFilter } from '../inputField';
import useCloudProvider from '../../hooks/useCloudProvider';

interface FieldOption {
  label: string;
  value: string;
}

type FieldType = 'select' | 'dateRange';

interface FilterField {
  key: string;
  label: string;
  type: FieldType;
  options?: FieldOption[];
}

interface FilterFormProps {
  show: boolean;
  onClose: () => void;
  fields: FilterField[];
  onApply: (filters: Record<string, any>) => void;
  onDateRangeChange?: (dateRange: { from: string; to: string }) => void;
  onCurrentChoosedAppChange?: any;
  dropdownValuesLoading?: boolean;
  showCloudProvider?: boolean;
  selectedCloud?: number;
  cloudProvidersList?: [];
  selectCloud?: (a: any) => void;
}

const getDefaultDateRange = () => {
  const today = new Date();
  const pastDate = new Date();
  // pastDate.setMonth(today.getMonth() - 3);
  pastDate.setDate(today.getDate() - 6);
  return { fromDate: pastDate, toDate: today };
};

const formatDate = (date: Date | null) => {
  if (!date) return '';
  return date.toLocaleDateString('en-GB');
};

const FilterForm = ({
  show,
  onClose,
  fields,
  onApply,
  onDateRangeChange,
  onCurrentChoosedAppChange,
  dropdownValuesLoading,
  showCloudProvider = false,
  selectedCloud = 0,
  cloudProvidersList = [],
  selectCloud = (a) => {}
}: FilterFormProps) => {
  // initialize state dynamically
  const initialState: Record<string, any> = {};
  fields.forEach((f) => {
    if (f.type === 'dateRange') {
      const { fromDate, toDate } = getDefaultDateRange();
      initialState[f.key] = { fromDate, toDate };
    } else {
      initialState[f.key] = '';
    }
  });

  const [filters, setFilters] = useState<Record<string, any>>(initialState);

  const handleDateChange = (fieldKey: string, which: 'fromDate' | 'toDate', date: Date | null) => {
    const newField = { ...filters[fieldKey], [which]: date };
    setFilters((prev) => ({ ...prev, [fieldKey]: newField }));

    if (newField.fromDate && newField.toDate && onDateRangeChange) {
      onDateRangeChange({
        from: formatDate(newField.fromDate),
        to: formatDate(newField.toDate)
      });
    }
  };

  const handleSelectChange = (fieldKey: string, value: string) => {
    setFilters((prev) => ({ ...prev, [fieldKey]: value }));
    // onCurrentChoosedAppChange(fieldKey === 'application' ? value : '');
    if (fieldKey === 'application') {
      onCurrentChoosedAppChange(value);
    }
  };

  const resetField = (field: FilterField) => {
    if (field.type === 'dateRange') {
      const { fromDate, toDate } = getDefaultDateRange();
      setFilters((prev) => ({ ...prev, [field.key]: { fromDate, toDate } }));

      if (onDateRangeChange) {
        onDateRangeChange({
          from: formatDate(fromDate),
          to: formatDate(toDate)
        });
      }
    } else {
      setFilters((prev) => ({ ...prev, [field.key]: '' }));
      onCurrentChoosedAppChange(field.key === 'application' && '');
    }
  };

  const resetAll = () => {
    setFilters(initialState);
    onCurrentChoosedAppChange('');
    selectCloud(0);

    // also fire dateRange reset if applicable
    fields.forEach((f) => {
      if (f.type === 'dateRange' && onDateRangeChange) {
        const { fromDate, toDate } = getDefaultDateRange();
        onDateRangeChange({
          from: formatDate(fromDate),
          to: formatDate(toDate)
        });
      }
    });
  };

  const applyFilters = () => {
    const applied: Record<string, any> = {};
    fields.forEach((f) => {
      if (f.type === 'dateRange') {
        applied[f.key] = {
          from: formatDate(filters[f.key].fromDate),
          to: formatDate(filters[f.key].toDate)
        };
      } else {
        applied[f.key] = filters[f.key];
      }

      if (selectedCloud) {
        applied['cloud_platform_id'] = selectedCloud;
      }
    });

    onApply(applied);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="filter-drawer"
      contentClassName="filter-drawer-content"
      backdrop={false}
      keyboard={false}
      id="filter_modal">
      <Modal.Header closeButton>
        <Modal.Title>Filter</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showCloudProvider && (
          <CloudProviderFilter
            cloudProvidersList={cloudProvidersList}
            selectedCloud={selectedCloud}
            selectCloud={selectCloud}
          />
        )}
        {fields.map((field) => (
          <div className="mb-3" key={field.key}>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label>{field.label}</Form.Label>
              <Button variant="link" size="sm" onClick={() => resetField(field)}>
                Reset
              </Button>
            </div>

            {field.type === 'dateRange' && (
              <Row>
                <Col>
                  <Form.Label className="date_labels">From</Form.Label>
                  <div className="input-group analytics_datepicker_container">
                    <DatePicker
                      selected={filters[field.key].fromDate}
                      onChange={(date) => handleDateChange(field.key, 'fromDate', date)}
                      dateFormat="dd/MM/yyyy"
                      className="form-control border-end-0"
                      placeholderText="Select date"
                      showYearDropdown
                      // maxDate={new Date()}
                      maxDate={filters[field.key].toDate || new Date()}
                    />
                    <span className="input-group-text bg-white">
                      <img src={darkCalenderIcon} alt="calendar" />
                    </span>
                  </div>
                </Col>
                <Col>
                  <Form.Label className="date_labels">To</Form.Label>
                  <div className="input-group analytics_datepicker_container">
                    <DatePicker
                      selected={filters[field.key].toDate}
                      onChange={(date) => handleDateChange(field.key, 'toDate', date)}
                      dateFormat="dd/MM/yyyy"
                      className="form-control border-end-0"
                      placeholderText="Select date"
                      showYearDropdown
                      minDate={filters[field.key].fromDate}
                      maxDate={new Date()}
                    />
                    <span className="input-group-text bg-white">
                      <img src={darkCalenderIcon} alt="calendar" />
                    </span>
                  </div>
                </Col>
              </Row>
            )}

            {field.type === 'select' && (
              <Form.Select
                value={filters[field.key]}
                onChange={(e) => handleSelectChange(field.key, e.target.value)}
                disabled={field.key === 'modelName' && dropdownValuesLoading ? true : false}>
                <option value="">Select {field.label}</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            )}
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button className="resetAll_btn" onClick={resetAll}>
          Reset All
        </Button>
        <Button variant="primary" onClick={applyFilters}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FilterForm;
